
/**
 * GSC VISUAL DSP - MOTION ENGINE v2.0
 * Transforme le flux vidéo en signal d'amplitude cinématique.
 */
export class MotionEngine {
    /**
     * Calcule la densité de mouvement entre deux frames (0-1)
     */
    static calculateMotionDensity(canvasA: HTMLCanvasElement, canvasB: HTMLCanvasElement): number {
        const ctxA = canvasA.getContext('2d', { willReadFrequently: true });
        const ctxB = canvasB.getContext('2d', { willReadFrequently: true });
        if (!ctxA || !ctxB) return 0;

        const dataA = ctxA.getImageData(0, 0, canvasA.width, canvasA.height).data;
        const dataB = ctxB.getImageData(0, 0, canvasB.width, canvasB.height).data;
        
        let diffCount = 0;
        const step = 25; // Optimisation pour le temps réel
        
        for (let i = 0; i < dataA.length; i += (4 * step)) {
            const r = Math.abs(dataA[i] - dataB[i]);
            const g = Math.abs(dataA[i+1] - dataB[i+1]);
            const b = Math.abs(dataA[i+2] - dataB[i+2]);
            if (r + g + b > 65) diffCount++;
        }
        
        return diffCount / (dataA.length / (4 * step));
    }

    /**
     * Extrait l'enveloppe de mouvement complète d'un fichier vidéo
     * Résolution : 10 points par seconde (suffisant pour la guitare)
     */
    static async getFullMotionEnvelope(videoFile: File, duration: number): Promise<number[]> {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            const url = URL.createObjectURL(videoFile);
            video.src = url;
            video.muted = true;
            video.playsInline = true;

            const envelope: number[] = [];
            const canvasPrev = document.createElement('canvas');
            const canvasCurr = document.createElement('canvas');
            const w = 160; const h = 90; // Basse résolution pour la rapidité
            canvasPrev.width = canvasCurr.width = w;
            canvasPrev.height = canvasCurr.height = h;
            
            const ctxPrev = canvasPrev.getContext('2d', { alpha: false });
            const ctxCurr = canvasCurr.getContext('2d', { alpha: false });

            video.onloadedmetadata = async () => {
                const stepSec = 0.1; // 10Hz sampling
                for (let t = 0; t < duration; t += stepSec) {
                    video.currentTime = Math.max(0, t - 0.04);
                    await new Promise(r => video.onseeked = r);
                    ctxPrev?.drawImage(video, 0, 0, w, h);

                    video.currentTime = t;
                    await new Promise(r => video.onseeked = r);
                    ctxCurr?.drawImage(video, 0, 0, w, h);

                    envelope.push(this.calculateMotionDensity(canvasPrev, canvasCurr));
                }
                URL.revokeObjectURL(url);
                resolve(envelope);
            };
        });
    }

    /**
     * Analyse une vidéo pour extraire le profil de vélocité visuelle aux moments clés
     */
    static async getMotionProfile(videoFile: File, timestamps: number[]): Promise<{time: number, velocity: number}[]> {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.src = URL.createObjectURL(videoFile);
            video.muted = true;
            video.playsInline = true;
            
            const results: {time: number, velocity: number}[] = [];
            const canvasPrev = document.createElement('canvas');
            const canvasCurr = document.createElement('canvas');
            
            video.onloadedmetadata = async () => {
                const w = 320; 
                const h = 180;
                canvasPrev.width = canvasCurr.width = w;
                canvasPrev.height = canvasCurr.height = h;
                const ctxPrev = canvasPrev.getContext('2d', { alpha: false });
                const ctxCurr = canvasCurr.getContext('2d', { alpha: false });

                for (const t of timestamps) {
                    const tStart = Math.max(0, t - 0.045);
                    video.currentTime = tStart;
                    await new Promise(r => video.onseeked = r);
                    ctxPrev?.drawImage(video, 0, 0, w, h);
                    
                    video.currentTime = t;
                    await new Promise(r => video.onseeked = r);
                    ctxCurr?.drawImage(video, 0, 0, w, h);
                    
                    const vel = this.calculateMotionDensity(canvasPrev, canvasCurr);
                    results.push({ time: t, velocity: vel });
                }
                URL.revokeObjectURL(video.src);
                resolve(results);
            };
        });
    }
}
